import { useEffect, useState } from "react";
import { Image, Text, View } from "react-native";
import { useBlocksUser } from "@/src/services/users.blocks.api";
import { firstUsableImageUrl } from "@/src/utils/image-url.utils";

type BlocksAvatarProps = Readonly<{
  userId?: string;
  // Shown while the Blocks lookup resolves or if the user has no image.
  fallbackImageUrl?: string | null;
  // Used for the initials placeholder when no image is available.
  firstName?: string;
  lastName?: string;
  name?: string;
  size?: number;
  className?: string;
}>;

const getInitials = (firstName?: string, lastName?: string, name?: string) => {
  const first = (firstName ?? "").trim();
  const last = (lastName ?? "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const source = (first || last || name || "").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const BlocksAvatar = ({
  userId,
  fallbackImageUrl,
  firstName,
  lastName,
  name,
  size = 32,
  className,
}: BlocksAvatarProps) => {
  const { data: user } = useBlocksUser(userId);

  /**
   * The IAM record wins when it has a picture, so a freshly saved one propagates
   * everywhere. It must not win when it is *blank*, which is the shape the user
   * list uses for "no picture": `??` let `""` through, so the caller's perfectly
   * good fallback was discarded and the avatar rendered an empty circle — no
   * image and no initials.
   */
  const uri = firstUsableImageUrl(user?.profileImageUrl, fallbackImageUrl);

  // A URL that 404s or 403s used to leave a blank hole, because `uri` was truthy
  // so the initials branch was never reached. Falling back on load failure keeps
  // the avatar meaningful when a stored file has gone away.
  const [failedUri, setFailedUri] = useState<string | null>(null);
  useEffect(() => {
    setFailedUri(null);
  }, [uri]);

  if (uri && uri !== failedUri) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: size / 2 }}
        className={className}
        onError={() => setFailedUri(uri)}
      />
    );
  }

  const initials = getInitials(
    firstName ?? user?.firstName,
    lastName ?? user?.lastName,
    name,
  );

  return (
    <View
      style={{ width: size, height: size, borderRadius: size / 2 }}
      className={`bg-primary items-center justify-center ${className ?? ""}`}
    >
      <Text
        className="text-white font-semibold"
        style={{ fontSize: Math.max(10, Math.round(size * 0.4)) }}
      >
        {initials}
      </Text>
    </View>
  );
};

export default BlocksAvatar;
